<!-- eslint-disable vue/no-v-html -->
<template>
  <!-- Publication result item max-container -->
  <div class="flex flex-col gap-y-2 text-gray-900 mb-8">
    <!-- Publication title with optional search term highlighting -->
    <h4 class="m-0!">
      <a
        :href="result.html_url"
        v-html="highlightedTitle"
      />
    </h4>

    <!-- Publication metadata (date and type) if available -->
    <h6 v-if="metaText" class="m-0! text-gray-700">{{ metaText }}</h6>

    <!-- Authors list with conditional linking -->
    <h5
      v-if="hasAuthors"
      class="text-gray-700"
    >By
      <template v-for="(author, index) in authors" :key="index">
        <!-- Author with URL - render as link -->
        <template v-if="author.author_url">
          <a :href="`/authors/${author.author_url}`" class="author">
            {{ author.first_name }} {{ author.last_name }}
          </a>
        </template>
        <!-- Author without URL - render as plain text -->
        <template v-else>
          <span class="author">
            {{ author.first_name }} {{ author.last_name }}
          </span>
        </template>
        <!-- Add comma separator between multiple authors (except after last) -->
        <span v-if="index < authors.length - 1">, </span>
      </template>
    </h5>

    <!-- Publication description -->
    <p itemprop="description">
      {{ result.description }}
    </p>

    <!-- Download link -->
    <a
      :href="result.html_url"
      class="link-cta" v-if="typeText === 'Webcast'">WATCH</a>
    <a
      :href="result.html_url"
      class="link-cta" v-else>DOWNLOAD</a>
  </div>
</template>


<script lang="ts" setup>

import { computed } from 'vue'
import type { ISearchResult } from './PublicationList.vue'

/**
 * Interface for author objects containing name and optional URL
 */
interface IAuthor {
  first_name: string
  last_name: string
  author_url?: string // Optional since some authors may not have URLs
}

/**
 * Component props
 */
const props = defineProps<{
  result: ISearchResult  // Publication search result data
  query: string         // Search query for highlighting
}>()

/**
 * Computed property that highlights search terms in the publication title
 * Wraps matching query terms in <b> tags for visual emphasis
 */
const highlightedTitle = computed(() => {
  const title = props.result.title || ''
  if (!props.query) return title
  const regex = new RegExp(`(${decodeURI(props.query)})`, 'gi')
  return (
    title.replace(regex, '<b>$1</b>')
  )
})

/**
 * Formats the publication date into a readable string
 * Returns empty string if no date or invalid date provided
 */
const publishedText = computed(() => {
  const val = props.result.date_published
  if (!val) return ''
  const date = new Date(val)
  if (!isNaN(date.getTime())) {
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    })
  }
  return String(val)
})

/**
 * Extracts and returns the publication type/asset type from metadata
 * Returns empty string if no type information available
 */
const typeText = computed(() => {
  const val = props.result.meta?.get_asset_type
  if (!val) return ''
  return String(val)
})

/**
 * Combines publication date and type into a formatted metadata string
 * Uses bullet separator between date and type when both are available
 */
const metaText = computed(() => {
  if (publishedText.value && typeText.value) {
    return `${publishedText.value} \u00a0\u00a0\u2022\u00a0\u00a0 ${typeText.value}`
  } else if (publishedText.value) {
    return publishedText.value
  } else if (typeText.value) {
    return typeText.value
  }
  return ''
})

/**
 * Processes and returns the authors array from the search result
 * Returns empty array if no authors or invalid authors data
 */
const authors = computed((): IAuthor[] => {
  if (!props.result.authors || !Array.isArray(props.result.authors)) {
    return []
  }
  return props.result.authors
})

/**
 * Determines if the publication has any authors to display
 * Used to conditionally show the authors section in template
 */
const hasAuthors = computed(() => {
  const hasAuth = authors.value && authors.value.length > 0
  return hasAuth
})
</script>
